import { LightbulbIcon, TrophyIcon, BadgeDollarSignIcon, HandshakeIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";

export default function BusinessImpact() {
  return (
    <section id="business-impact" className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <span className="inline-block px-3 py-1 text-sm font-medium text-primary-700 bg-primary-100 rounded-full mb-4">For Employers</span>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">Smart Business, Stronger Communities</h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Hiring justice-involved individuals isn't just socially responsible—it's a strategic business advantage.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-r from-amber-400 to-amber-600 text-white shadow-md mb-4">
              <TrophyIcon className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Higher Retention</h3>
            <p className="text-slate-600 mb-4">
              83% retention rate after one year—significantly above industry averages.
            </p>
            <div className="mt-auto pt-4">
              <span className="text-2xl font-bold text-primary-600">2.3x</span>
              <p className="text-sm text-slate-500">longer employment duration</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-r from-blue-400 to-blue-600 text-white shadow-md mb-4">
              <LightbulbIcon className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Dedicated Workers</h3>
            <p className="text-slate-600 mb-4">
              Exceptional motivation, punctuality, and appreciation for opportunity.
            </p>
            <div className="mt-auto pt-4">
              <span className="text-2xl font-bold text-primary-600">91%</span>
              <p className="text-sm text-slate-500">attendance rate</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-r from-green-400 to-green-600 text-white shadow-md mb-4">
              <BadgeDollarSignIcon className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Financial Incentives</h3>
            <p className="text-slate-600 mb-4">
              Access Work Opportunity Tax Credits of up to $9,600 per eligible hire.
            </p>
            <div className="mt-auto pt-4">
              <span className="text-2xl font-bold text-primary-600">$5,200</span>
              <p className="text-sm text-slate-500">avg. savings per hire</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 border border-slate-100 flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-r from-purple-400 to-purple-600 text-white shadow-md mb-4">
              <HandshakeIcon className="h-8 w-8" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">Cultural Growth</h3>
            <p className="text-slate-600 mb-4">
              Strengthen your workplace culture with diverse perspectives and experiences.
            </p>
            <div className="mt-auto pt-4">
              <span className="text-2xl font-bold text-primary-600">78%</span>
              <p className="text-sm text-slate-500">report improved company culture</p>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-8">
            Our employer partners consistently report that hiring justice-involved individuals through StaffAbility results in some of their most loyal, hardworking, and dedicated employees.
          </p>
          <Button asChild className="bg-gradient-to-r from-primary-500 to-primary-600 shadow-md">
            <Link href="/hire-from-us">
              Become an Employer Partner
            </Link>
          </Button>
        </div>

        <div className="mt-16 bg-slate-50 p-8 rounded-xl border border-slate-200">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-2/3 md:pr-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                "StaffAbility hires have been among our most reliable and productive team members. They've strengthened our workforce and our bottom line."
              </h3>
              <div className="flex items-center">
                <div className="mr-4">
                  <p className="font-semibold text-slate-900">James Wilson</p>
                  <p className="text-sm text-slate-500">CEO, Metro Manufacturing</p>
                </div>
              </div>
            </div>
            <div className="md:w-1/3 flex justify-center mt-6 md:mt-0">
              <div className="bg-white p-6 rounded-lg shadow-md border border-slate-100">
                <div className="flex items-center justify-center space-x-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-primary-600">94%</p>
                    <p className="text-xs text-slate-500">Would Hire<br />Again</p>
                  </div>
                  <div className="h-10 w-px bg-slate-200"></div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-primary-600">12.5x</p>
                    <p className="text-xs text-slate-500">Return On<br />Investment</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}